import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import type { TicketResourceLink } from "@msuite/utils/types";
import { ResourceLinkCard } from "@/components/cards/resource-link-card";

/** Props Interface */
interface ResourceLinksModalProps extends ModalProps {
	resourceLinks: TicketResourceLink[];
}

/**
 * Read-only list of the resource links already attached to a record, rendered as the same
 * `ResourceLinkCard` the ticket form uses. It exists so a compact indicator can defer the
 * actual list to a modal instead of spending card space on it — linking and unlinking stay
 * in the ticket form, which owns the mutations.
 *
 * @param resourceLinks - The links to list, each carrying its resource.
 */
export function ResourceLinksModal({ isOpen, onClose, resourceLinks }: ResourceLinksModalProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Render */
	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={t("resource_links.resource_links")}
			width="500px"
			data-cy="resource-links-modal"
		>
			<Modal.Body>
				{!resourceLinks.length && <BlankSlate label={t("resource_links.no_resource_links")} />}
				<div className="v-stack gap-2">
					{resourceLinks.map((resourceLink) => (
						<ResourceLinkCard
							key={resourceLink.id}
							url={resourceLink.resourceLink.link}
							name={resourceLink.resourceLink.name}
						/>
					))}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<div className="self-end h-stack gap-4 ml-auto">
					<Button
						variant="primary"
						onClick={onClose}
					>
						{t("general.done")}
					</Button>
				</div>
			</Modal.Footer>
		</Modal>
	);
}
